import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { 
  ArrowLeft, 
  Home,
  AlertCircle,
  Check
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from "../components/ui/select";
import axios from "axios";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const exploitationTypes = [
  { value: "residence_principale", label: "Résidence principale" },
  { value: "residence_secondaire", label: "Résidence secondaire" },
  { value: "investissement_locatif", label: "Investissement locatif" },
  { value: "conciergerie", label: "Conciergerie / Gestion" },
  { value: "autre", label: "Autre" },
];

const PropertyFormPage = () => {
  const navigate = useNavigate();
  const { orderId, propertyId } = useParams();
  const isEditing = !!propertyId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);
  const [property, setProperty] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    owner_name: "",
    street: "",
    postal_code: "",
    city: "",
    country: "France",
    surface: "",
    particularities: "",
    exploitation_type: "" });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEditing) {
      fetchProperty();
    }
  }, [propertyId]);

  const fetchProperty = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/properties/${propertyId}`, {
        withCredentials: true });
      setProperty(data);
      setFormData({
        name: data.name || "",
        owner_name: data.owner_name || "",
        street: data.risk_address?.street || "",
        postal_code: data.risk_address?.postal_code || "",
        city: data.risk_address?.city || "",
        country: data.risk_address?.country || "France",
        surface: data.surface?.toString() || "",
        particularities: data.particularities || "",
        exploitation_type: data.exploitation_type || "" });
    } catch (error) {
      toast.error("Erreur lors du chargement du logement");
      navigate("/logements");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) newErrors.name = "Le nom du logement est requis";
    if (!formData.owner_name.trim()) newErrors.owner_name = "Le nom du propriétaire est requis";
    if (!formData.street.trim()) newErrors.street = "L'adresse est requise";
    if (!formData.postal_code.trim()) newErrors.postal_code = "Le code postal est requis";
    if (!formData.city.trim()) newErrors.city = "La ville est requise";
    if (!formData.surface || parseFloat(formData.surface) <= 0) newErrors.surface = "La surface est requise";
    if (!formData.exploitation_type) newErrors.exploitation_type = "Le type d'exploitation est requis";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      toast.error("Veuillez corriger les erreurs du formulaire");
      return;
    }

    setSaving(true);
    
    const payload = {
      name: formData.name,
      owner_name: formData.owner_name,
      risk_address: {
        street: formData.street,
        postal_code: formData.postal_code,
        city: formData.city,
        country: formData.country },
      surface: parseFloat(formData.surface),
      particularities: formData.particularities,
      exploitation_type: formData.exploitation_type };

    try {
      if (isEditing) {
        await axios.put(`${API_URL}/api/properties/${propertyId}`, payload, {
          withCredentials: true });
        toast.success("Logement mis à jour");
      } else {
        await axios.post(`${API_URL}/api/properties/create`, {
          ...payload,
          order_id: orderId }, {
          withCredentials: true });
        toast.success("Logement créé");
      }
      navigate("/logements");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleValidate = async () => {
    if (!validate()) {
      toast.error("Veuillez compléter tous les champs obligatoires avant de valider");
      return;
    }

    setValidating(true);
    
    try {
      // First save the property
      const payload = {
        name: formData.name,
        owner_name: formData.owner_name,
        risk_address: {
          street: formData.street,
          postal_code: formData.postal_code,
          city: formData.city,
          country: formData.country },
        surface: parseFloat(formData.surface),
        particularities: formData.particularities,
        exploitation_type: formData.exploitation_type };

      if (isEditing) {
        await axios.put(`${API_URL}/api/properties/${propertyId}`, payload, {
          withCredentials: true });
      }

      // Then validate
      await axios.post(`${API_URL}/api/properties/${propertyId}/validate`, {}, {
        withCredentials: true });
      
      toast.success("Logement validé ! Vous pouvez maintenant activer votre protection.");
      navigate("/logements");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la validation");
    } finally {
      setValidating(false);
    }
  };

  const canValidate = isEditing && property?.status === "draft";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00B8E4]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 font-['DM_Sans']">
      {/* Header */}
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo-hostprotect.png" alt="HostProtect" className="h-9 w-auto" />
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="py-8 sm:py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/logements" 
            className="inline-flex items-center gap-2 text-slate-600 hover:text-[#19294C] mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour à mes logements
          </Link>

          {/* Warning Banner */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
            <div className="flex gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                <strong>Important :</strong> L'adresse du logement correspond à l'adresse du risque assuré. Elle doit être exacte et complète pour permettre l'activation de votre protection.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-lg overflow-hidden" data-testid="property-form-card">
            {/* Header */}
            <div className="bg-[#19294C] px-6 sm:px-8 py-6 text-white">
              <div className="flex items-center gap-3">
                <Home className="w-8 h-8" />
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold font-['Outfit']">
                    {isEditing ? "Modifier le logement" : "Nouveau logement"}
                  </h1>
                  <p className="text-blue-200 text-sm mt-1">
                    {isEditing ? "Modifiez les informations de votre logement" : "Renseignez les informations de votre logement"}
                  </p>
                </div>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSave} className="p-6 sm:p-8 space-y-6">
              {/* Property Info */}
              <div className="space-y-4">
                <h2 className="text-lg font-semibold text-[#19294C] font-['Outfit']">
                  Informations du logement
                </h2>
                
                <div>
                  <Label htmlFor="name" className="text-slate-700">
                    Nom du logement <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    placeholder="Ex: Appartement Paris 11"
                    className={errors.name ? "border-red-500" : ""}
                    data-testid="property-name-input"
                  />
                  {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                </div>

                <div>
                  <Label htmlFor="owner_name" className="text-slate-700">
                    Nom du propriétaire / exploitant <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="owner_name"
                    value={formData.owner_name}
                    onChange={(e) => handleChange("owner_name", e.target.value)}
                    placeholder="Ex: Jean Dupont"
                    className={errors.owner_name ? "border-red-500" : ""}
                    data-testid="property-owner-input"
                  />
                  {errors.owner_name && <p className="text-red-500 text-sm mt-1">{errors.owner_name}</p>}
                </div>
              </div>

              {/* Risk Address */}
              <div className="space-y-4 pt-4 border-t border-slate-100">
                <h2 className="text-lg font-semibold text-[#19294C] font-['Outfit']">
                  Adresse du risque assuré
                </h2>
                
                <div>
                  <Label htmlFor="street" className="text-slate-700">
                    Adresse complète <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="street"
                    value={formData.street}
                    onChange={(e) => handleChange("street", e.target.value)}
                    placeholder="Ex: 15 rue de la Paix"
                    className={errors.street ? "border-red-500" : ""}
                    data-testid="property-street-input"
                  />
                  {errors.street && <p className="text-red-500 text-sm mt-1">{errors.street}</p>}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="postal_code" className="text-slate-700">
                      Code postal <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="postal_code"
                      value={formData.postal_code}
                      onChange={(e) => handleChange("postal_code", e.target.value)}
                      placeholder="75011"
                      className={errors.postal_code ? "border-red-500" : ""}
                      data-testid="property-postal-input"
                    />
                    {errors.postal_code && <p className="text-red-500 text-sm mt-1">{errors.postal_code}</p>}
                  </div>
                  <div>
                    <Label htmlFor="city" className="text-slate-700">
                      Ville <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => handleChange("city", e.target.value)}
                      placeholder="Paris"
                      className={errors.city ? "border-red-500" : ""}
                      data-testid="property-city-input"
                    />
                    {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
                  </div>
                </div>

                <div>
                  <Label htmlFor="country" className="text-slate-700">Pays</Label>
                  <Input
                    id="country"
                    value={formData.country}
                    onChange={(e) => handleChange("country", e.target.value)}
                    placeholder="France"
                    data-testid="property-country-input"
                  />
                </div>
              </div>

              {/* Property Details */}
              <div className="space-y-4 pt-4 border-t border-slate-100">
                <h2 className="text-lg font-semibold text-[#19294C] font-['Outfit']">
                  Caractéristiques
                </h2>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="surface" className="text-slate-700">
                      Surface (m²) <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="surface"
                      type="number"
                      min="1"
                      value={formData.surface}
                      onChange={(e) => handleChange("surface", e.target.value)}
                      placeholder="50"
                      className={errors.surface ? "border-red-500" : ""}
                      data-testid="property-surface-input"
                    />
                    {errors.surface && <p className="text-red-500 text-sm mt-1">{errors.surface}</p>}
                  </div>
                  <div>
                    <Label htmlFor="exploitation_type" className="text-slate-700">
                      Type d'exploitation <span className="text-red-500">*</span>
                    </Label>
                    <Select 
                      value={formData.exploitation_type} 
                      onValueChange={(value) => handleChange("exploitation_type", value)}
                    >
                      <SelectTrigger className={errors.exploitation_type ? "border-red-500" : ""} data-testid="property-type-select">
                        <SelectValue placeholder="Sélectionnez..." />
                      </SelectTrigger>
                      <SelectContent>
                        {exploitationTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.exploitation_type && <p className="text-red-500 text-sm mt-1">{errors.exploitation_type}</p>}
                  </div>
                </div>

                <div>
                  <Label htmlFor="particularities" className="text-slate-700">
                    Particularités (optionnel)
                  </Label>
                  <Textarea
                    id="particularities"
                    value={formData.particularities}
                    onChange={(e) => handleChange("particularities", e.target.value)}
                    placeholder="Ex: Piscine, jardin, équipements spécifiques..."
                    rows={3}
                    data-testid="property-particularities-input"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="pt-6 border-t border-slate-100 flex flex-col sm:flex-row gap-3">
                <Button
                  type="submit"
                  disabled={saving}
                  variant="outline"
                  className="flex-1"
                  data-testid="save-property-btn"
                >
                  {saving ? "Enregistrement..." : "Enregistrer le brouillon"}
                </Button>
                
                {canValidate && (
                  <Button
                    type="button"
                    onClick={handleValidate}
                    disabled={validating}
                    className="flex-1 bg-[#00B8E4] hover:bg-[#00a5ce] text-white"
                    data-testid="validate-property-btn"
                  >
                    {validating ? (
                      "Validation..."
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Valider et préparer l'activation
                      </>
                    )}
                  </Button>
                )}

                {!isEditing && (
                  <Button
                    type="submit"
                    disabled={saving}
                    className="flex-1 bg-[#00B8E4] hover:bg-[#00a5ce] text-white"
                    data-testid="create-property-btn"
                  >
                    {saving ? "Création..." : "Créer le logement"}
                  </Button>
                )}
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default PropertyFormPage;
